// External libraries
import { action, actionOn } from 'easy-peasy';
import { isBoolean } from '@checkout/react-components';

// Internal modules
import { $logger } from '@/lib/logger';
import { features } from '@/lib/features';
import { getLocale } from '@/lib/globals';
import {
  getAddressValidationPayload,
  getBaseAddressFields,
  getDefaultEmailState,
  getDefaultInputState,
  getDefaultKycStateOverride,
  revalidatePassword,
  wasInfoSecFieldChanged,
} from './utils';
import { AUTO_COMPLETE_TYPE } from './constants';

const DEFAULT_PHONE_METADATA = {
  isConfirmed: false,
  verificationId: '',
};

/**
 * @function getKycFields
 * @returns {object}
 * @description Returns an object with kycFields
 */
const getKycFields = () => {
  const kycFields = features.config.kycFields
    // change first letter to be lower cased
    .map(fieldName => fieldName.charAt(0).toLowerCase() + fieldName.slice(1))
    .reduce((accObj, field) => {
      const override = getDefaultKycStateOverride(field);
      accObj[field] = getDefaultInputState(override);

      return accObj;
    }, {});
  return kycFields;
};

const updateAddressField = (state, data) => {
  const { addressType, field, isBatchUpdate, payload } = data;

  if (!/^(billing|shipping)Address$/.test(addressType)) {
    return $logger.warn('weasley_update_address_invalid_type', {
      addressType,
    });
  }

  if (isBatchUpdate) {
    const updatedAddressFields = Object.entries(payload).reduce(
      (accum, [fieldName, fieldData]) => {
        const hasIsDirtyProperty = isBoolean(
          state[addressType]?.[fieldName]?.isDirty,
        );
        const isDirty =
          state[addressType]?.[fieldName]?.isDirty ||
          fieldData.isDirty ||
          Boolean(fieldData.value);

        accum[fieldName] = {
          ...state[addressType][fieldName],
          ...fieldData,
          ...(hasIsDirtyProperty && { isDirty }),
        };
        return accum;
      },
      {},
    );

    state[addressType] = {
      ...state[addressType],
      ...updatedAddressFields,
    };

    return;
  }

  const hasIsDirtyProperty = isBoolean(state[addressType]?.[field]?.isDirty);
  const isDirty =
    state[addressType]?.[field]?.isDirty ||
    payload.isDirty ||
    Boolean(payload.value);

  state[addressType][field] = {
    ...state[addressType][field],
    ...payload,
    ...(hasIsDirtyProperty && { isDirty }),
  };
};

const updateAutoCompleteType = type => (state, data) => {
  const { addressType } = data;
  state[addressType] = {
    ...state[addressType],
    autoCompleteType: {
      value: type,
    },
  };
};

const updateIsManipulated = value => (state, data) => {
  const { addressType } = data;
  state[addressType] = {
    ...state[addressType],
    isManipulated: {
      value,
    },
  };
};

const getDefaultOnboardOption = () => {
  const isGuest = features.isActive('isGuestEligible');
  const isPoma = features.isActive('isPOMAEligible');
  const isSignupIneligible = features.isActive('shouldDisableSignup');
  const overrides = {
    isValid: !isGuest, // Initial value is '' for guest, which isn't valid
    value: 'signup', // Default for forced signup
  };

  if (isPoma || isGuest) {
    // POMA checkbox is unchecked by default, so onboardOption is guest
    // For optional guest neither radio button is checked on page load
    overrides.value = isPoma ? 'guest' : '';
  }

  // Default to guest when signup is ineligible
  if (isSignupIneligible) {
    overrides.errorCode = '';
    overrides.isValid = true;
    overrides.value = 'guest';
  }

  return getDefaultInputState(overrides);
};

const getAddressState = () => ({
  // State
  billingAddress: {
    ...getBaseAddressFields(),
    autoCompleteType: { value: AUTO_COMPLETE_TYPE.MANUAL },
    isManipulated: { value: false },
    meta: { hasValidationError: false },
  },
  shareAddressWithDonatee: {
    value: !features.isActive('isDonationOptionallyShareAddress'),
  },
  shippingAddress: {
    autoCompleteType: { value: AUTO_COMPLETE_TYPE.MANUAL },
    country: getDefaultInputState({
      errorCode: '',
      isValid: true,
      value: getLocale().country,
    }),
    firstName: getDefaultInputState(),
    isManipulated: { value: false },
    lastName: getDefaultInputState(),
    meta: { hasValidationError: false },
    ...getBaseAddressFields(),
  },
  shipToBilling: {
    hasChanged: false,
    value: true,
  },

  /**
   * This variable signifies whether we should bypass the address
   * normalization flow. The default value is false and value would be set to
   * true when user completes address normalization flow. The current use
   * case for it is when user has completed the address normalization flow but
   * is brought back to the form again due to an OAS error. The user then
   * submits the form again but this time we want to skip the normalization
   * flow if the user has not updated the shipping address (or billing address
   * if shipToBilling is true).
   */
  bypassAddressNormalization: false, // eslint-disable-line sort-keys
});

const createAddressActions = () => ({
  // Actions
  updateAddressField: action((state, data) => {
    updateIsManipulated(true)(state, data);
    updateAddressField(state, data);
  }),

  updateAddressValidationError: action((state, data) => {
    updateAddressField(state, {
      addressType: data.addressType,
      isBatchUpdate: true,
      payload: getAddressValidationPayload(data),
    });
  }),

  updateAllAddressFieldsUsingANS: action((state, data) => {
    updateAddressField(state, data);
    updateAutoCompleteType(AUTO_COMPLETE_TYPE.ANS)(state, data);
    updateIsManipulated(false)(state, data);
  }),

  updateAllAddressFieldsUsingGoogleAutocomplete: action((state, data) => {
    updateAddressField(state, data);
    updateAutoCompleteType(AUTO_COMPLETE_TYPE.GOOGLE)(state, data);
    updateIsManipulated(false)(state, data);
  }),

  updateAllAddressFieldsUsingMerchantPrefilledData: action((state, data) => {
    updateAddressField(state, data);
    updateAutoCompleteType(AUTO_COMPLETE_TYPE.MERCHANT_PREFILLED)(state, data);
    updateIsManipulated(false)(state, data);
  }),

  updateBypassAddressNormalization: action((state, payload) => {
    state.bypassAddressNormalization = Boolean(payload);
  }),

  updateShareAddressWithDonatee: action((state, payload) => {
    state.shareAddressWithDonatee = { value: payload };
  }),

  updateShipToBilling: action((state, payload) => {
    state.shipToBilling = {
      hasChanged: true,
      value: payload,
    };
  }),

  // Listeners
  // eslint-disable-next-line sort-keys
  bypassAddressNormalizationListener: actionOn(
    // Target resolver
    actions => actions.updateAddressField,
    (state, target) => {
      const { addressType } = target.payload;
      const shipTo = state.shipToBilling.value
        ? 'billingAddress'
        : 'shippingAddress';

      // If the user changes the shipping address then set the
      // bypass address normalization flag to `false`
      if (
        features.isActive('hasAddressNormalization') &&
        addressType === shipTo &&
        state.bypassAddressNormalization
      ) {
        state.bypassAddressNormalization = false;
      }
    },
  ),
});

const getInputState = () => ({
  bankAccountNumber: getDefaultInputState(),
  bankAccountType: {
    value: 'iban',
  },
  bankIban: getDefaultInputState(),
  bankSortCode: getDefaultInputState(),
  cardCvv: getDefaultInputState(),
  cardExpiry: getDefaultInputState(),
  cardNumber: getDefaultInputState({
    cardType: '',
    cobrand: '',
  }),
  cardProductClass: getDefaultInputState(),
  currencyConversionSelection: '',
  dialingCode: getDefaultInputState({
    errorCode: '',
    isValid: true,
    value: getLocale().country,
  }),
  email: getDefaultEmailState(),
  firstName: getDefaultInputState(),
  guestAgreeToTerms: getDefaultInputState({
    isValid: features.isNotActive('hasGuestTermsAndConditionsCheckbox'),
    value: false,
  }),
  installmentTerm: getDefaultInputState(),
  lastName: getDefaultInputState(),
  marketingOptIn: getDefaultInputState({
    errorCode: '',
    isValid: true,
    value:
      !features.isActive('hasMarketingOptInCheckbox') ||
      features.isActive('isMarketingOptInCheckedByDefault'),
  }),
  onboardOption: getDefaultOnboardOption(),
  password: getDefaultInputState(),
  phone: {
    ...getDefaultInputState(),
    meta: DEFAULT_PHONE_METADATA,
  },
  phoneType: getDefaultInputState({
    errorCode: '',
    isValid: true,
    value: 'MOBILE',
  }),
  sepaMandate: getDefaultInputState({
    isValid: features.isNotActive('hasSepaMandate'),
    value: false,
  }),
  shippingInsuranceSelected: false,
  signupAgreeToTerms: getDefaultInputState({
    isValid: features.isNotActive('hasSignupTermsAndConditionsCheckbox'),
    value: false,
  }),
  ...getKycFields(),
});

const createInputActions = () => ({
  // Actions
  updateCurrencyConversionSelection: action((state, selection) => {
    state.currencyConversionSelection = selection;
  }),

  updateNonAddressField: action((state, { field, payload }) => {
    const isDirty =
      state[field]?.isDirty || payload.isDirty || Boolean(payload.value);
    state[field] = {
      ...state[field],
      ...payload,
      isDirty,
    };
  }),

  updateShippingInsuranceSelected: action((state, payload) => {
    state.shippingInsuranceSelected = Boolean(payload);
  }),

  // Listeners
  // If the user enters their password before entering the values that the
  // info sec rule applies to (name, dob) then we need to revalidate when
  // those fields are updated.
  // eslint-disable-next-line sort-keys
  onUpdateNonAddressField: actionOn(
    actions => actions.updateNonAddressField,
    (state, target) => {
      if (
        features.isActive('hasPasswordInfoSecRule') &&
        wasInfoSecFieldChanged(target.payload.field)
      ) {
        const { errors, isValid } = revalidatePassword(state);

        if (isValid !== state.password.isValid) {
          state.password = {
            ...state.password,
            displayError: state.displayError && !isValid,
            errorCode: errors[0] || '',
            isValid,
          };
        }
      }
    },
  ),

  onUpdateStreetNameAndHouseNumber: actionOn(
    actions => actions.updateAddressField,
    (state, target) => {
      const isStreetNameOrHouseNumberField = [
        'streetName',
        'houseNumber',
      ].includes(target.payload.field);

      if (!isStreetNameOrHouseNumberField) {
        return;
      }

      const addressType = target.payload.addressType;
      const { houseNumber, streetName } = state[addressType];
      const newLine1 = `${streetName.value}, ${houseNumber.value}`;
      state[addressType].line1 = {
        ...state[addressType].line1,
        isDirty: true,
        isValid: houseNumber.isValid && streetName.isValid,
        value: newLine1,
      };
    },
  ),

  resetNamesErrorOnChangeNationality: actionOn(
    actions => actions.updateNonAddressField,
    (state, target) => {
      if (target.payload.field !== 'nationality') {
        return;
      }

      const defaultValues = {
        displayError: false,
        error: '',
        errorCode: '',
        isValid: true,
      };

      const firstNameField = state.firstName;
      if (
        firstNameField.errorCode === 'INVALID_FIRST_NAME' &&
        firstNameField.displayError
      ) {
        state.firstName = {
          ...state.firstName,
          ...defaultValues,
        };
      }

      const lastNameField = state.lastName;
      if (
        lastNameField.errorCode === 'INVALID_LAST_NAME' &&
        lastNameField.displayError
      ) {
        state.lastName = {
          ...state.lastName,
          ...defaultValues,
        };
      }
    },
  ),

  resetPhoneMetadataWhenValueChanged: actionOn(
    actions => actions.updateNonAddressField,
    (state, target) => {
      const isConfirmedPhoneUpdateAction =
        target.payload.field === 'phone' && target.payload.meta === undefined;

      if (!isConfirmedPhoneUpdateAction) {
        state.phone = {
          ...state.phone,
          meta: DEFAULT_PHONE_METADATA,
        };
      }
    },
  ),
});

const getFormState = overrides => ({
  ...getAddressState(),
  ...getInputState(),
  ...overrides,
});

const formModelFactory = () => ({
  ...getFormState(),
  ...createAddressActions(),
  ...createInputActions(),
});

export { formModelFactory, getFormState, AUTO_COMPLETE_TYPE };
